<script setup lang="ts">
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { computed, reactive, watch } from 'vue'
import { Loader } from 'lucide-vue-next'
import { usePaginatedRadiusServersQuery } from '@/modules/radius/queries/v1/server'
import { PaginatedTanstackQueryParams } from '@/lib/types/v1'
import ErrorAlert from '@/components/ui/alert/ErrorAlert.vue'
import Button from '../ui/button/Button.vue'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { radiusServerParamKey } from '@/modules/radius/routes'

const props = defineProps<{
    active: boolean
}>()

const params = reactive<PaginatedTanstackQueryParams>({
    enabled: false,
})

const {
    data: paginatedServers,
    isFetching,
    refetch,
    error,
} = usePaginatedRadiusServersQuery(params)
const servers = computed(() => {
    return paginatedServers.value?.data || []
})

watch(
    () => props.active,
    async () => {
        if (props.active) {
            params.enabled = true
        }
    }
)
</script>

<template>
    <AccordionItem value="radius" class="border-b-0 group">
        <AccordionTrigger
            class="py-2 px-4 text-white hover:no-underline hover:bg-neutral-600/50 data-[state=open]:bg-zinc-800 items-center gap-0"
        >
            <div class="flex items-center justify-between flex-1">
                <div class="flex items-center">
                    <div class="w-6">
                        <FontAwesomeIcon :icon="['fal', 'shield-alt']" class="size-4!" />
                    </div>
                    <div class="ml-3 text-xs">Radius</div>
                </div>
                <div class="flex items-center">
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger as-child>
                                <Button
                                    variant="icon"
                                    size="icon-xs"
                                    class="h-4 w-4 mr-2 hidden group-hover:flex"
                                    @click.stop="refetch"
                                >
                                    <FontAwesomeIcon
                                        icon="fal fa-sync-alt"
                                        class="h-3 w-3"
                                        :class="{ 'animate-spin': isFetching }"
                                    />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Refresh</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            </div>
        </AccordionTrigger>
        <AccordionContent class="pb-0 text-xs text-white">
            <div v-if="isFetching" class="flex items-center space-x-2 pl-[52px] py-2">
                <Loader class="mr-2 h-4 w-4 shrink-0 opacity-50 animate-spin" />
                <span>Loading...</span>
            </div>
            <div v-else class="flex flex-col">
                <ErrorAlert :error="error" class="p-2" />
                <template v-for="server in servers" :key="server.uuid">
                    <RouterLink
                        :to="{
                            name: 'radius.server.users.index',
                            params: {
                                [radiusServerParamKey]: server.uuid,
                            },
                            query: {
                                server_title: server.title,
                            },
                        }"
                        class="pl-[52px] py-2 pr-4 hover:bg-neutral-600/50 truncate"
                        >{{ server.title }}</RouterLink
                    >
                </template>
            </div>
        </AccordionContent>
    </AccordionItem>
</template>

<style scoped>
.router-link-exact-active {
    background-color: rgba(255, 255, 255, 0.1);
}
</style>
